/**
 * Редактор сообщения Activity на базе Lexical.
 * Управляет вводом, mentions (`@/#`), slash-командами и рендером активного
 * контекста/поверхности над инпутом через `ActivityContextView`.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { ActionIcon, Box, Group, Loader, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { UserAvatar } from '@ayocore/platform/identity/client/components/UserAvatar';
import { useUsersController } from '@ayocore/platform/identity/client/UsersController';
import { uploadFile } from '@ayocore/platform/client/utils';
import { IconPaperclip, IconSend, IconSlash, IconX } from '@tabler/icons-react';
import logger from '@ayocore/platform/common/helpers/logger';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { InitialConfigType } from '@lexical/react/LexicalComposer';
import { mergeRegister } from '@lexical/utils';
import { getActivityScopeTitle } from '@ayocore/activity/client/extensions';
import {
  COMMAND_PRIORITY_HIGH,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  type LexicalEditor,
  type NodeKey,
} from 'lexical';
import {
  ActivityLexicalMentionNode,
  $createActivityLexicalMentionNode,
  $isActivityLexicalMentionNode,
} from './ActivityLexicalMentionNode';
import {
  ActivityLexicalContactMentionNode,
  $createActivityLexicalContactMentionNode,
  $isActivityLexicalContactMentionNode,
} from './ActivityLexicalContactMentionNode';
import type { ActivityMentionDeal } from '../../types/activity-mention-deal';
import type { ActivityMentionUser } from '../../types/activity-mention-user';
import type { ActivityMentionContact } from '../../types/activity-mention-contact';
import { useActivityScopeMentionSearch } from '../../hooks/useActivityScopeMentionSearch';
import { useActivityMentionUsersSearch } from '../../hooks/useActivityMentionUsersSearch';
import { useActivityContactSearch } from '../../hooks/useActivityContactSearch';
import type { CommentMention } from '@ayocore/comments/common/types/comment-mention';
import type { CommentContactMention } from '@ayocore/comments/common/types/comment-contact-mention';
import type { ActivityCommentScope } from '@ayocore/activity/common/types/activity-comment-scope';
import type {
  ActivitySlashCommand,
  ActivityComposerCommandApi,
} from '../../types/activity-slash-command';
import { getActivityChatCommands } from '@ayocore/activity/client/extensions';
import { ActivityContextView } from '../context/ActivityContextView';
import type { ActivityContext } from '../../types/activity-context';
import type { ActivityComposerController } from '../../controllers/ActivityComposerController';
import type { ActivityComposerInsertFragment } from '../../types/activity-composer-fragment';
import composerClasses from './ActivityChatComposer.module.css';

type ActivityChatComposerProps = {
  composerController: ActivityComposerController;
  onSendComment: (
    content: string,
    mentionedUserIds?: number[],
    mentionedUserMentions?: CommentMention[],
    scope?: ActivityCommentScope | null,
    mentionedContactUuids?: string[],
    mentionedContactMentions?: CommentContactMention[],
    fileIds?: number[]
  ) => Promise<boolean> | boolean;
  sendingComment: boolean;
  replyPreview?: string | null;
  onCancelReply?: () => void;
  mentionUsers?: ActivityMentionUser[];
  threadEntityType?: string | null;
  threadEntityUuid?: string | null;
  onOpenSlashCommand?: (commandId: string) => boolean;
  scopePreset?: ActivityCommentScope | null;
  onScopePresetConsumed?: () => void;
  activeContext?: ActivityContext | null;
  onCloseActiveContext?: () => void;
};

type MentionTrigger = '@' | '#';

type MentionContext = {
  trigger: MentionTrigger;
  nodeKey: NodeKey;
  startOffset: number;
  endOffset: number;
  query: string;
};

/**
 * Состояние inline-picker'а контактных лиц. Активируется slash-командой
 * `/contact` — в этот момент инпут очищается (slash-команда всегда стоит
 * в начале draft'а), а весь дальнейший ввод трактуется как поисковый
 * запрос по контактам. На выборе контакта чип вставляется в начало
 * инпута, picker закрывается.
 */
type ContactPickerContext = {
  query: string;
};

type MentionSnapshot = {
  draft: string;
  mentionedUserIds: number[];
  mentionRanges: CommentMention[];
  mentionedContactUuids: string[];
  contactMentionRanges: CommentContactMention[];
  mentionContext: MentionContext | null;
};

/**
 * Подсказка в popup'е активного mention-контекста (`@` — пользователи,
 * `#` — сделки). Контактные лица сюда не попадают — для них отдельный
 * inline-picker (см. `ContactPickerContext`).
 */
type MentionSuggestion =
  | {
      kind: 'user';
      key: string;
      label: string;
      user: ActivityMentionUser;
    }
  | {
      kind: 'deal';
      key: string;
      label: string;
      deal: ActivityMentionDeal;
    };

type SlashCommandSuggestion = {
  key: string;
  command: ActivitySlashCommand;
  label: string;
  description?: string;
};

/**
 * Локальный clientId — для адресации чипа до того, как сервер вернул `id`.
 * Когда `uploading=true`, `id` ещё null. После успеха `id` заполняется и
 * чип становится «отправляемым» (попадёт в `file_ids` при send).
 */
type AttachedFile = {
  clientId: string;
  id: number | null;
  name: string;
  size: number;
  uploading: boolean;
  error?: string;
};

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isMentionBoundary(char: string | undefined): boolean {
  if (!char) return true;
  return /\s|[([{"'«]/.test(char);
}

function normalizeDealMentionText(label: string): string {
  const normalized = label.trim();
  if (!normalized) return '#';
  return normalized.startsWith('#') ? normalized : `#${normalized}`;
}

// Заголовок scope-контекста формирует ядро расширений Activity
// (диспетчеризация через реестр дескрипторов сущностей).

function arraysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function stringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function contactMentionRangesEqual(
  a: CommentContactMention[],
  b: CommentContactMention[]
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (
      a[i].uuid !== b[i].uuid ||
      a[i].label !== b[i].label ||
      a[i].start !== b[i].start ||
      a[i].end !== b[i].end
    ) {
      return false;
    }
  }
  return true;
}

function mentionContextEqual(a: MentionContext | null, b: MentionContext | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.trigger === b.trigger &&
    a.nodeKey === b.nodeKey &&
    a.startOffset === b.startOffset &&
    a.endOffset === b.endOffset &&
    a.query === b.query
  );
}

function mentionRangesEqual(a: CommentMention[], b: CommentMention[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (
      left.user_id !== right.user_id ||
      left.start !== right.start ||
      left.end !== right.end ||
      left.label !== right.label
    ) {
      return false;
    }
  }
  return true;
}

function readSlashCommandQuery(value: string): string | null {
  if (!value.startsWith('/')) {
    return null;
  }

  const query = value.slice(1);
  if (query.length === 0) {
    return '';
  }

  if (/\s/.test(query)) {
    return null;
  }

  return query.trim().toLowerCase();
}

function readMentionSnapshot(): MentionSnapshot {
  const root = $getRoot();
  const draft = root.getTextContent();

  const mentionedSet = new Set<number>();
  const mentionRanges: CommentMention[] = [];
  const mentionedContactSet = new Set<string>();
  const contactMentionRanges: CommentContactMention[] = [];
  let offset = 0;
  root.getAllTextNodes().forEach(node => {
    const text = node.getTextContent();
    const length = text.length;
    if ($isActivityLexicalMentionNode(node)) {
      const mentionId = node.getMentionId();
      if (mentionId > 0) {
        mentionedSet.add(mentionId);
        mentionRanges.push({
          user_id: mentionId,
          label: node.getMentionLabel(),
          start: offset,
          end: offset + length,
        });
      }
    } else if ($isActivityLexicalContactMentionNode(node)) {
      const contactUuid = node.getContactUuid();
      if (contactUuid) {
        mentionedContactSet.add(contactUuid);
        contactMentionRanges.push({
          uuid: contactUuid,
          label: node.getContactLabel(),
          start: offset,
          end: offset + length,
        });
      }
    }
    offset += length;
  });
  const mentionedUserIds = Array.from(mentionedSet).sort((a, b) => a - b);
  const mentionedContactUuids = Array.from(mentionedContactSet).sort();

  const selection = $getSelection();
  if (
    !$isRangeSelection(selection) ||
    !selection.isCollapsed() ||
    selection.anchor.type !== 'text'
  ) {
    return {
      draft,
      mentionedUserIds,
      mentionRanges,
      mentionedContactUuids,
      contactMentionRanges,
      mentionContext: null,
    };
  }

  const anchorNode = selection.anchor.getNode();
  if (
    !$isTextNode(anchorNode) ||
    $isActivityLexicalMentionNode(anchorNode) ||
    $isActivityLexicalContactMentionNode(anchorNode)
  ) {
    return {
      draft,
      mentionedUserIds,
      mentionRanges,
      mentionedContactUuids,
      contactMentionRanges,
      mentionContext: null,
    };
  }

  const endOffset = selection.anchor.offset;
  const text = anchorNode.getTextContent();
  const leftPart = text.slice(0, endOffset);
  const rawTriggerCandidates: Array<{ trigger: MentionTrigger; index: number }> = [
    { trigger: '@', index: leftPart.lastIndexOf('@') },
    { trigger: '#', index: leftPart.lastIndexOf('#') },
  ];
  const triggerCandidates = rawTriggerCandidates.filter(item => item.index >= 0);
  if (triggerCandidates.length === 0) {
    return {
      draft,
      mentionedUserIds,
      mentionRanges,
      mentionedContactUuids,
      contactMentionRanges,
      mentionContext: null,
    };
  }
  triggerCandidates.sort((a, b) => b.index - a.index);
  const activeTrigger = triggerCandidates[0];

  const beforeTrigger = activeTrigger.index > 0 ? leftPart[activeTrigger.index - 1] : undefined;
  if (!isMentionBoundary(beforeTrigger)) {
    return {
      draft,
      mentionedUserIds,
      mentionRanges,
      mentionedContactUuids,
      contactMentionRanges,
      mentionContext: null,
    };
  }

  const mentionBody = leftPart.slice(activeTrigger.index + 1);
  if (mentionBody.length > 0 && /\s/.test(mentionBody)) {
    return {
      draft,
      mentionedUserIds,
      mentionRanges,
      mentionedContactUuids,
      contactMentionRanges,
      mentionContext: null,
    };
  }

  return {
    draft,
    mentionedUserIds,
    mentionRanges,
    mentionedContactUuids,
    contactMentionRanges,
    mentionContext: {
      trigger: activeTrigger.trigger,
      nodeKey: anchorNode.getKey(),
      startOffset: activeTrigger.index,
      endOffset,
      query: mentionBody.trim().toLowerCase(),
    },
  };
}

function ActivityCaptureEditorPlugin({ onReady }: { onReady: (editor: LexicalEditor) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    onReady(editor);
  }, [editor, onReady]);

  return null;
}

function ActivityEditablePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

function ActivitySuggestionsKeyBindingsPlugin({
  suggestionsOpen,
  suggestionsCount,
  highlightedIndex,
  setHighlightedIndex,
  onSelectSuggestionByIndex,
  onCloseSuggestions,
  onSend,
}: {
  suggestionsOpen: boolean;
  suggestionsCount: number;
  highlightedIndex: number;
  setHighlightedIndex: Dispatch<SetStateAction<number>>;
  onSelectSuggestionByIndex: (index: number) => void;
  onCloseSuggestions: () => void;
  onSend: () => void;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          KEY_ARROW_DOWN_COMMAND,
          event => {
            if (!suggestionsOpen || suggestionsCount === 0) return false;
            event?.preventDefault();
            setHighlightedIndex(prev => (prev + 1) % suggestionsCount);
            return true;
          },
          COMMAND_PRIORITY_HIGH
        ),
        editor.registerCommand(
          KEY_ARROW_UP_COMMAND,
          event => {
            if (!suggestionsOpen || suggestionsCount === 0) return false;
            event?.preventDefault();
            setHighlightedIndex(prev => (prev - 1 + suggestionsCount) % suggestionsCount);
            return true;
          },
          COMMAND_PRIORITY_HIGH
        ),
        editor.registerCommand(
          KEY_TAB_COMMAND,
          event => {
            if (!suggestionsOpen || suggestionsCount === 0) return false;
            event?.preventDefault();
            onSelectSuggestionByIndex(highlightedIndex);
            return true;
          },
          COMMAND_PRIORITY_HIGH
        ),
        editor.registerCommand(
          KEY_ENTER_COMMAND,
          event => {
            if (suggestionsOpen && suggestionsCount > 0) {
              if (event?.ctrlKey || event?.metaKey) return false;
              event?.preventDefault();
              onSelectSuggestionByIndex(highlightedIndex);
              return true;
            }

            // Shift+Enter — перенос строки (стандарт мессенджеров).
            // Plain Enter и Ctrl/Cmd+Enter — отправка.
            if (event?.shiftKey) {
              return false;
            }

            event?.preventDefault();
            onSend();
            return true;
          },
          COMMAND_PRIORITY_HIGH
        ),
        editor.registerCommand(
          KEY_ESCAPE_COMMAND,
          event => {
            if (!suggestionsOpen) return false;
            event?.preventDefault();
            onCloseSuggestions();
            return true;
          },
          COMMAND_PRIORITY_HIGH
        )
      ),
    [
      editor,
      suggestionsOpen,
      suggestionsCount,
      highlightedIndex,
      setHighlightedIndex,
      onSelectSuggestionByIndex,
      onCloseSuggestions,
      onSend,
    ]
  );

  return null;
}

export function ActivityChatComposer({
  composerController,
  onSendComment,
  sendingComment,
  replyPreview,
  onCancelReply,
  mentionUsers = [],
  threadEntityType,
  threadEntityUuid,
  onOpenSlashCommand,
  scopePreset,
  onScopePresetConsumed,
  activeContext = null,
  onCloseActiveContext,
}: ActivityChatComposerProps) {
  const [editor, setEditor] = useState<LexicalEditor | null>(null);
  const [draft, setDraft] = useState('');
  const { getUser } = useUsersController();
  const [mentionedUserIds, setMentionedUserIds] = useState<number[]>([]);
  const [mentionRanges, setMentionRanges] = useState<CommentMention[]>([]);
  const [mentionedContactUuids, setMentionedContactUuids] = useState<string[]>([]);
  const [contactMentionRanges, setContactMentionRanges] = useState<CommentContactMention[]>([]);
  const [mentionContext, setMentionContext] = useState<MentionContext | null>(null);
  const [contactPickerContext, setContactPickerContext] =
    useState<ContactPickerContext | null>(null);
  const [selectedScope, setSelectedScope] = useState<ActivityCommentScope | null>(null);
  const [isScopeBoundToDraft, setIsScopeBoundToDraft] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachedClientIdSeqRef = useRef(0);
  const composerSignals = useSyncExternalStore(
    composerController.subscribe,
    composerController.getSnapshot,
    composerController.getSnapshot
  );
  const normalizedMentionQuery = mentionContext?.query.trim().toLowerCase() ?? '';
  const normalizedContactPickerQuery =
    contactPickerContext?.query.trim().toLowerCase() ?? '';
  const { remoteMentionUsers, mentionsLoading } = useActivityMentionUsersSearch({
    enabled: mentionContext?.trigger === '@',
    query: normalizedMentionQuery,
  });
  const { contactItems: remoteMentionContacts, contactsLoading } = useActivityContactSearch({
    enabled: Boolean(contactPickerContext),
    query: normalizedContactPickerQuery,
    threadEntityType,
    threadEntityUuid,
  });
  const { scopeMentionItems: remoteMentionDeals, scopeMentionLoading: dealsLoading } =
    useActivityScopeMentionSearch({
      enabled: mentionContext?.trigger === '#',
      query: normalizedMentionQuery,
      threadEntityType,
      threadEntityUuid,
    });

  const initialConfig = useMemo<InitialConfigType>(
    () => ({
      namespace: 'crm-activity-chat-composer',
      nodes: [ActivityLexicalMentionNode, ActivityLexicalContactMentionNode],
      editable: true,
      onError: error => {
        throw error;
      },
      theme: {
        // Сбрасываем дефолтный margin у `<p>`, иначе при первом mount'е
        // пустой paragraph добавляет ~28px к высоте инпута (визуально
        // выглядит как «инпут в 2 строки»). После первого clear'а Lexical
        // удаляет paragraph и высота нормализуется — но без override'а
        // первый рендер всё равно показывает завышенную высоту.
        paragraph: composerClasses.paragraph,
      },
    }),
    []
  );

  const mentionSuggestions = useMemo<MentionSuggestion[]>(() => {
    if (!mentionContext) return [];
    if (mentionContext.trigger === '#') {
      const query = normalizedMentionQuery;
      return remoteMentionDeals
        .filter(deal => {
          const label = deal.label.toLowerCase();
          const searchText = (deal.searchText ?? deal.label).toLowerCase();
          if (!query) return true;
          return label.includes(query) || searchText.includes(query);
        })
        .slice(0, 8)
        .map(deal => ({
          kind: 'deal' as const,
          key: `deal:${deal.uuid}`,
          label: normalizeDealMentionText(deal.label),
          deal,
        }));
    }

    // `@` — только пользователи. Контактные лица доступны через slash-команду
    // `/contact` (см. contactPickerContext ниже).
    const query = normalizedMentionQuery;
    const filteredLocal = mentionUsers.filter(user => {
      const label = user.label.toLowerCase();
      const searchText = (user.searchText ?? user.label).toLowerCase();
      if (!query) return true;
      return label.includes(query) || searchText.includes(query);
    });
    const byId = new Map<number, ActivityMentionUser>();
    filteredLocal.forEach(user => byId.set(user.id, user));
    remoteMentionUsers.forEach(user => {
      if (!byId.has(user.id)) {
        byId.set(user.id, user);
      }
    });
    return Array.from(byId.values())
      .slice(0, 8)
      .map(user => ({
        kind: 'user' as const,
        key: `user:${user.id}`,
        label: user.label,
        user,
      }));
  }, [
    mentionContext,
    mentionUsers,
    remoteMentionUsers,
    remoteMentionDeals,
    normalizedMentionQuery,
  ]);

  const mentionOpen = Boolean(mentionContext);
  const slashCommands = useMemo(() => getActivityChatCommands(), []);
  const threadCommandContext = useMemo(
    () => ({
      threadEntityType,
      threadEntityUuid,
    }),
    [threadEntityType, threadEntityUuid]
  );
  const slashCommandContext = useMemo(
    () => ({
      ...threadCommandContext,
      source: 'slash' as const,
      event: null,
      payload: null,
    }),
    [threadCommandContext]
  );
  const slashQuery = useMemo(() => readSlashCommandQuery(draft), [draft]);
  const slashSuggestions = useMemo<SlashCommandSuggestion[]>(() => {
    if (slashQuery === null) return [];

    const query = slashQuery.trim().toLowerCase();
    return slashCommands
      .filter(command => (command.isAvailable ? command.isAvailable(slashCommandContext) : true))
      .filter(command => {
        if (!query) return true;
        const haystack = [
          command.trigger,
          command.label,
          command.description ?? '',
          ...(command.aliases ?? []),
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
      .map(command => ({
        key: command.id,
        command,
        label: `/${command.trigger} — ${command.label}`,
        description: command.description,
      }));
  }, [slashQuery, slashCommandContext, slashCommands]);
  const slashMenuOpen = !mentionOpen && slashQuery !== null;
  // Picker контактных лиц — отдельная панель, активируется slash-командой
  // `/contact`. Приоритет: standard mention (`@`/`#`) > slash menu > contact picker.
  const contactPickerOpen = Boolean(contactPickerContext) && !mentionOpen && !slashMenuOpen;
  const contactPickerSuggestions = useMemo(() => {
    if (!contactPickerOpen) return [];
    const q = normalizedContactPickerQuery;
    return remoteMentionContacts
      .filter(contact => {
        if (!q) return true;
        const label = contact.label.toLowerCase();
        const searchText = (contact.searchText ?? contact.label).toLowerCase();
        return label.includes(q) || searchText.includes(q);
      })
      .slice(0, 8)
      .map(contact => ({
        key: `contact:${contact.uuid}`,
        label: contact.label,
        contact,
      }));
  }, [contactPickerOpen, remoteMentionContacts, normalizedContactPickerQuery]);
  const suggestionsOpen = mentionOpen || slashMenuOpen || contactPickerOpen;
  const suggestionsCount = mentionOpen
    ? mentionSuggestions.length
    : slashMenuOpen
      ? slashSuggestions.length
      : contactPickerSuggestions.length;
  const composerDisabled = sendingComment;
  const scopeTitle = useMemo(() => getActivityScopeTitle(selectedScope), [selectedScope]);

  const insertFragment = useCallback(
    (fragment: ActivityComposerInsertFragment) => {
      if (!editor) return;

      if (fragment.kind === 'user_mention') {
        const mentionId = Number(fragment.userId);
        const mentionLabel = typeof fragment.label === 'string' ? fragment.label.trim() : '';
        if (!Number.isFinite(mentionId) || mentionId <= 0 || !mentionLabel) return;

        requestAnimationFrame(() => {
          editor.focus();
          editor.update(() => {
            let selection = $getSelection();
            if (!$isRangeSelection(selection)) {
              $getRoot().selectEnd();
              selection = $getSelection();
            }
            if (!$isRangeSelection(selection)) return;

            selection.insertNodes([
              $createActivityLexicalMentionNode(Math.trunc(mentionId), mentionLabel),
              $createTextNode(' '),
            ]);
          });
        });
      }
    },
    [editor]
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [
    mentionContext?.trigger,
    mentionContext?.nodeKey,
    mentionContext?.startOffset,
    mentionSuggestions.length,
    slashQuery,
    slashSuggestions.length,
    contactPickerOpen,
    contactPickerSuggestions.length,
  ]);

  // Сброс picker'а при смене thread'а — иначе попап покажется в чужом контексте.
  useEffect(() => {
    setContactPickerContext(null);
  }, [threadEntityType, threadEntityUuid]);

  useEffect(() => {
    setSelectedScope(null);
    setIsScopeBoundToDraft(false);
  }, [threadEntityType, threadEntityUuid]);

  useEffect(() => {
    if (!replyPreview) return;
    requestAnimationFrame(() => {
      editor?.focus();
    });
  }, [replyPreview, editor]);

  useEffect(() => {
    if (composerSignals.focusRequestSeq <= 0) return;
    // Двойной RAF помогает переждать закрытие dropdown/меню,
    // которое может вернуть фокус в trigger после клика.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        editor?.focus();
      });
    });
  }, [composerSignals.focusRequestSeq, editor]);

  useEffect(() => {
    const pendingInsertFragment = composerSignals.pendingInsertFragment;
    if (!pendingInsertFragment) return;
    insertFragment(pendingInsertFragment.fragment);
    composerController.consumeInsertFragment(pendingInsertFragment.requestId);
  }, [composerSignals.pendingInsertFragment, insertFragment, composerController]);

  useEffect(() => {
    if (scopePreset === undefined) return;

    if (scopePreset === null) {
      setSelectedScope(null);
      setIsScopeBoundToDraft(false);
      onScopePresetConsumed?.();
      return;
    }

    const entityType =
      typeof scopePreset.entity_type === 'string' ? scopePreset.entity_type.trim() : '';
    const entityUuid =
      typeof scopePreset.entity_uuid === 'string' ? scopePreset.entity_uuid.trim() : '';
    const label = typeof scopePreset.label === 'string' ? scopePreset.label.trim() : '';
    if (!entityType || !entityUuid) {
      onScopePresetConsumed?.();
      return;
    }

    setSelectedScope({
      entity_type: entityType,
      entity_uuid: entityUuid,
      ...(label ? { label } : {}),
    });
    setIsScopeBoundToDraft(false);
    onScopePresetConsumed?.();
    requestAnimationFrame(() => {
      editor?.focus();
    });
  }, [scopePreset, onScopePresetConsumed, editor]);

  const clearComposer = useCallback(() => {
    if (!editor) {
      setDraft('');
      setMentionedUserIds([]);
      setMentionRanges([]);
      setMentionedContactUuids([]);
      setContactMentionRanges([]);
      setMentionContext(null);
      setContactPickerContext(null);
      setSelectedScope(null);
      setIsScopeBoundToDraft(false);
      setHighlightedIndex(0);
      return;
    }

    editor.update(() => {
      $getRoot().clear();
    });
    setDraft('');
    setMentionedUserIds([]);
    setMentionRanges([]);
    setMentionedContactUuids([]);
    setContactMentionRanges([]);
    setMentionContext(null);
    setContactPickerContext(null);
    setSelectedScope(null);
    setIsScopeBoundToDraft(false);
    setHighlightedIndex(0);
    setAttachedFiles([]);
    requestAnimationFrame(() => {
      editor.focus();
    });
  }, [editor]);

  /**
   * Загружает выбранные файлы — оптимистично показывает чипы с лоадером,
   * после успеха записывает `id`. Падение одного файла не валит остальные.
   */
  const handleAttachFiles = useCallback(async (files: FileList | File[]): Promise<void> => {
    const list = Array.from(files);
    if (list.length === 0) return;

    const startSeq = attachedClientIdSeqRef.current;
    const pending: AttachedFile[] = list.map((file, idx) => ({
      clientId: `attach-${startSeq + idx}`,
      id: null,
      name: file.name,
      size: file.size,
      uploading: true,
    }));
    attachedClientIdSeqRef.current = startSeq + list.length;
    setAttachedFiles(prev => [...prev, ...pending]);

    await Promise.all(
      list.map(async (file, idx) => {
        const clientId = pending[idx].clientId;
        try {
          const result = await uploadFile(file, { directory: 'comments' });
          setAttachedFiles(prev =>
            prev.map(f => {
              if (f.clientId !== clientId) return f;
              if (result.success && result.file) {
                return { ...f, id: result.file.id, uploading: false };
              }
              return { ...f, uploading: false, error: result.error ?? 'Ошибка загрузки' };
            })
          );
        } catch (error) {
          logger.error('[ActivityChatComposer] uploadFile failed', { error });
          setAttachedFiles(prev =>
            prev.map(f =>
              f.clientId === clientId
                ? { ...f, uploading: false, error: 'Ошибка загрузки' }
                : f
            )
          );
        }
      })
    );
  }, []);

  const handleRemoveAttachedFile = useCallback((clientId: string) => {
    setAttachedFiles(prev => prev.filter(f => f.clientId !== clientId));
  }, []);

  const handleOpenFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const hasUploadingFiles = useMemo(
    () => attachedFiles.some(f => f.uploading),
    [attachedFiles]
  );
  const successfulFileIds = useMemo(
    () =>
      attachedFiles
        .filter(f => !f.uploading && !f.error && typeof f.id === 'number' && f.id > 0)
        .map(f => f.id as number),
    [attachedFiles]
  );

  const send = useCallback(async (): Promise<void> => {
    const normalizedContent = draft.trim();
    if (!normalizedContent || sendingComment) return;
    // Не отправляем пока хоть один файл грузится — иначе можно потерять id.
    if (hasUploadingFiles) return;

    const sent = await onSendComment(
      normalizedContent,
      mentionedUserIds,
      mentionRanges,
      selectedScope,
      mentionedContactUuids,
      contactMentionRanges,
      successfulFileIds.length > 0 ? successfulFileIds : undefined
    );
    if (!sent) return;
    clearComposer();
  }, [
    draft,
    sendingComment,
    hasUploadingFiles,
    onSendComment,
    mentionedUserIds,
    mentionRanges,
    mentionedContactUuids,
    contactMentionRanges,
    selectedScope,
    successfulFileIds,
    clearComposer,
  ]);

  const selectUserMention = useCallback(
    (user: ActivityMentionUser) => {
      if (!editor || !mentionContext) return;

      editor.update(() => {
        const selection = $getSelection();
        if (
          !$isRangeSelection(selection) ||
          !selection.isCollapsed() ||
          selection.anchor.type !== 'text'
        ) {
          return;
        }
        if (selection.anchor.key !== mentionContext.nodeKey) return;

        const targetNode = $getNodeByKey(mentionContext.nodeKey);
        if (!$isTextNode(targetNode) || $isActivityLexicalMentionNode(targetNode)) return;

        const endOffset = selection.anchor.offset;
        if (endOffset < mentionContext.startOffset) return;

        selection.setTextNodeRange(targetNode, mentionContext.startOffset, targetNode, endOffset);

        selection.insertNodes([
          $createActivityLexicalMentionNode(user.id, user.label),
          $createTextNode(' '),
        ]);
      });

      setHighlightedIndex(0);
      requestAnimationFrame(() => {
        editor.focus();
      });
    },
    [editor, mentionContext]
  );

  const selectDealMention = useCallback(
    (deal: ActivityMentionDeal) => {
      if (!editor || !mentionContext) return;

      const mentionText = normalizeDealMentionText(deal.label);
      editor.update(() => {
        const selection = $getSelection();
        if (
          !$isRangeSelection(selection) ||
          !selection.isCollapsed() ||
          selection.anchor.type !== 'text'
        ) {
          return;
        }
        if (selection.anchor.key !== mentionContext.nodeKey) return;

        const targetNode = $getNodeByKey(mentionContext.nodeKey);
        if (!$isTextNode(targetNode) || $isActivityLexicalMentionNode(targetNode)) return;

        const endOffset = selection.anchor.offset;
        if (endOffset < mentionContext.startOffset) return;

        selection.setTextNodeRange(targetNode, mentionContext.startOffset, targetNode, endOffset);

        selection.insertNodes([$createTextNode(mentionText), $createTextNode(' ')]);
      });

      setSelectedScope({
        entity_type: deal.entity_type,
        entity_uuid: deal.uuid,
        label: deal.label,
      });
      setIsScopeBoundToDraft(true);
      setHighlightedIndex(0);
      requestAnimationFrame(() => {
        editor.focus();
      });
    },
    [editor, mentionContext]
  );

  const selectMentionByIndex = useCallback(
    (index: number) => {
      const candidate = mentionSuggestions[index];
      if (!candidate) return;
      if (candidate.kind === 'user') {
        selectUserMention(candidate.user);
        return;
      }
      selectDealMention(candidate.deal);
    },
    [mentionSuggestions, selectUserMention, selectDealMention]
  );

  /**
   * Вставка чипа контактного лица из inline-picker'а. Очищаем поисковый
   * запрос (всё содержимое инпута) и вставляем чип меншна с пробелом —
   * пользователь продолжает писать комментарий.
   */
  const selectContactFromPicker = useCallback(
    (contact: ActivityMentionContact) => {
      if (!editor || !contactPickerContext) return;

      editor.update(() => {
        $getRoot().clear();
        $getRoot().selectEnd();
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        selection.insertNodes([
          $createActivityLexicalContactMentionNode(contact.uuid, contact.label),
          $createTextNode(' '),
        ]);
      });

      setContactPickerContext(null);
      setHighlightedIndex(0);
      requestAnimationFrame(() => {
        editor.focus();
      });
    },
    [editor, contactPickerContext]
  );

  /**
   * Открытие inline-picker'а контактных лиц. slash-команда `/контакт*` всегда
   * стоит в начале draft'а (см. readSlashCommandQuery), поэтому безопасно
   * полностью очистить инпут — пользователь не теряет содержимого.
   * Дальнейший ввод трактуется как поисковый запрос (см. OnChangePlugin).
   */
  const openContactPicker = useCallback(() => {
    if (!editor) return;
    editor.update(() => {
      $getRoot().clear();
    });
    setContactPickerContext({ query: '' });
    setHighlightedIndex(0);
    requestAnimationFrame(() => {
      editor.focus();
    });
  }, [editor]);

  /**
   * API, доступный inline-командам слэш-меню. Передаётся в `inlineHandler`
   * команды (см. ActivityComposerCommandApi). Расширять по реальной нужде.
   */
  /**
   * Очищает инпут и вставляет один символ-триггер — `/`, `@` или `#`.
   * Используется для кнопки slash-меню и inline-команд `/user`/`/deal`,
   * которые подменяют ручной ввод триггера. После insertText
   * `OnChangePlugin` сам подхватит trigger и откроет соответствующий popup.
   */
  const insertTriggerChar = useCallback(
    (trigger: '/' | '@' | '#') => {
      if (!editor) return;
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        root.selectEnd();
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertText(trigger);
        }
      });
      requestAnimationFrame(() => {
        editor.focus();
      });
    },
    [editor]
  );

  const handleToggleSlashMenu = useCallback(() => {
    if (slashMenuOpen) {
      if (!editor) return;
      editor.update(() => {
        $getRoot().clear();
      });
      requestAnimationFrame(() => {
        editor.focus();
      });
    } else {
      insertTriggerChar('/');
    }
  }, [slashMenuOpen, editor, insertTriggerChar]);
  const openUserMention = useCallback(() => insertTriggerChar('@'), [insertTriggerChar]);
  const openDealMention = useCallback(() => insertTriggerChar('#'), [insertTriggerChar]);

  /**
   * API, доступный inline-командам слэш-меню (см. ActivityComposerCommandApi).
   * Расширять по реальной нужде.
   */
  const composerCommandApi = useMemo<ActivityComposerCommandApi>(
    () => ({
      openContactPicker,
      openUserMention,
      openDealMention,
    }),
    [openContactPicker, openUserMention, openDealMention]
  );

  const selectSlashCommandByIndex = useCallback(
    (index: number) => {
      const suggestion = slashSuggestions[index];
      if (!suggestion) return;

      // Inline-команда: выполняется через API композера, внешний
      // обработчик НЕ вызывается. Композер не знает деталей команды.
      if (suggestion.command.inlineHandler) {
        suggestion.command.inlineHandler(composerCommandApi);
        return;
      }

      // Обычный путь: делегируем внешнему обработчику (модалка и т.п.).
      if (!onOpenSlashCommand) return;
      const opened = onOpenSlashCommand(suggestion.command.id);
      if (!opened) return;
      clearComposer();
    },
    [slashSuggestions, clearComposer, onOpenSlashCommand, composerCommandApi]
  );

  const selectContactPickerByIndex = useCallback(
    (index: number) => {
      const suggestion = contactPickerSuggestions[index];
      if (!suggestion) return;
      selectContactFromPicker(suggestion.contact);
    },
    [contactPickerSuggestions, selectContactFromPicker]
  );

  const selectSuggestionByIndex = useCallback(
    (index: number) => {
      if (mentionOpen) {
        selectMentionByIndex(index);
        return;
      }
      if (slashMenuOpen) {
        selectSlashCommandByIndex(index);
        return;
      }
      if (contactPickerOpen) {
        selectContactPickerByIndex(index);
      }
    },
    [
      mentionOpen,
      slashMenuOpen,
      contactPickerOpen,
      selectMentionByIndex,
      selectSlashCommandByIndex,
      selectContactPickerByIndex,
    ]
  );

  const closeSuggestions = useCallback(() => {
    if (mentionOpen) {
      setMentionContext(null);
      return;
    }
    if (contactPickerOpen) {
      // Закрываем picker; набранный пользователем текст оставляем — он мог
      // передумать и продолжает писать обычный комментарий.
      setContactPickerContext(null);
      return;
    }
    clearComposer();
  }, [mentionOpen, contactPickerOpen, clearComposer]);

  // Click-outside: закрытие contact picker'а кликом мимо композера.
  // Ref ставим на внешний div композера (обёртка инпута + popup'а) —
  // клики внутри (popup, инпут, кнопки) не закрывают picker.
  // Слушаем `click`, а не `mousedown`: иначе бы перегоняли React'овский
  // onMouseDown на айтемах popup'а — click фазится после, когда React
  // уже отработал selectContactFromPicker.
  const composerWrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!contactPickerOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const wrapper = composerWrapperRef.current;
      if (!wrapper) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrapper.contains(target)) return;
      setContactPickerContext(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contactPickerOpen]);

  return (
    <Stack gap='xs' m='sm'>
      <Box ref={composerWrapperRef} style={{ position: 'relative' }}>
        {suggestionsOpen ? (
          <Paper
            withBorder
            shadow='md'
            radius='md'
            style={{
              position: 'absolute',
              left: 0,
              right: 42,
              bottom: 'calc(100% + 6px)',
              zIndex: 4,
            }}
          >
            <ScrollArea.Autosize mah={180}>
              <Stack gap={2} p={4}>
                {mentionOpen
                  ? mentionSuggestions.map((suggestion, index) => {
                      const active = index === highlightedIndex;
                      const cachedUser =
                        suggestion.kind === 'user' ? getUser(suggestion.user.id) : undefined;
                      return (
                        <Box
                          key={suggestion.key}
                          role='button'
                          tabIndex={-1}
                          onMouseDown={event => {
                            event.preventDefault();
                            if (suggestion.kind === 'user') {
                              selectUserMention(suggestion.user);
                            } else {
                              selectDealMention(suggestion.deal);
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            borderRadius: 6,
                            padding: '6px 8px',
                            background: active ? 'var(--mantine-color-blue-0)' : 'transparent',
                            color: active ? 'var(--mantine-color-blue-9)' : 'inherit',
                          }}
                        >
                          <Group gap={8} wrap='nowrap'>
                            {suggestion.kind === 'user' ? (
                              <UserAvatar
                                avatarThumbUrl={cachedUser?.avatarThumbUrl}
                                name={cachedUser?.name ?? suggestion.label}
                                size='sm'
                              />
                            ) : null}
                            <Text size='sm' fw={500} lineClamp={1}>
                              {suggestion.label}
                            </Text>
                          </Group>
                        </Box>
                      );
                    })
                  : slashMenuOpen
                    ? slashSuggestions.map((suggestion, index) => {
                        const active = index === highlightedIndex;
                        return (
                          <Box
                            key={suggestion.key}
                            role='button'
                            tabIndex={-1}
                            onMouseDown={event => {
                              event.preventDefault();
                              selectSlashCommandByIndex(index);
                            }}
                            style={{
                              cursor: 'pointer',
                              borderRadius: 6,
                              padding: '6px 8px',
                              background: active
                                ? 'var(--mantine-color-blue-0)'
                                : 'transparent',
                              color: active ? 'var(--mantine-color-blue-9)' : 'inherit',
                            }}
                          >
                            <Text size='sm' fw={500} lineClamp={1}>
                              {suggestion.label}
                            </Text>
                            {suggestion.description ? (
                              <Text size='xs' c={active ? 'blue.8' : 'dimmed'} lineClamp={1}>
                                {suggestion.description}
                              </Text>
                            ) : null}
                          </Box>
                        );
                      })
                    : contactPickerSuggestions.map((suggestion, index) => {
                        const active = index === highlightedIndex;
                        return (
                          <Box
                            key={suggestion.key}
                            role='button'
                            tabIndex={-1}
                            onMouseDown={event => {
                              event.preventDefault();
                              selectContactFromPicker(suggestion.contact);
                            }}
                            style={{
                              cursor: 'pointer',
                              borderRadius: 6,
                              padding: '6px 8px',
                              background: active
                                ? 'var(--mantine-color-violet-0)'
                                : 'transparent',
                              color: active ? 'var(--mantine-color-violet-9)' : 'inherit',
                            }}
                          >
                            <Text size='sm' fw={500} lineClamp={1}>
                              {suggestion.label}
                            </Text>
                          </Box>
                        );
                      })}
                {suggestionsCount === 0 ? (
                  <Text size='xs' c='dimmed' px={8} py={6}>
                    {mentionOpen
                      ? mentionContext?.trigger === '#'
                        ? dealsLoading
                          ? 'Поиск сделок...'
                          : 'Сделка не найдена'
                        : mentionsLoading
                          ? 'Поиск пользователей...'
                          : 'Пользователь не найден'
                      : slashMenuOpen
                        ? 'Команда не найдена'
                        : contactsLoading
                          ? 'Поиск контактов...'
                          : 'Контакт не найден'}
                  </Text>
                ) : null}
                {contactPickerOpen ? (
                  <Text size='10px' c='dimmed' px={8} py={4} ta='right'>
                    Esc — отменить
                  </Text>
                ) : null}
              </Stack>
            </ScrollArea.Autosize>
          </Paper>
        ) : null}

        <Box
          style={{
            position: 'relative',
            borderRadius: 10,
            border:
              selectedScope || activeContext
                ? '2px solid var(--mantine-color-green-6)'
                : '1px solid var(--mantine-color-gray-3)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
            background: 'var(--mantine-color-body)',
            minHeight: 36,
          }}
        >
          {replyPreview || activeContext ? (
            <Box
              style={{
                padding: '6px 8px',
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                background: 'var(--mantine-color-gray-0)',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {replyPreview ? (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Text
                    size='xs'
                    style={{
                      minWidth: 0,
                      flex: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {replyPreview}
                  </Text>
                  <ActionIcon
                    variant='subtle'
                    color='gray'
                    size='sm'
                    aria-label='Отменить ответ'
                    onClick={onCancelReply}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Box>
              ) : null}

              {activeContext ? (
                <ActivityContextView context={activeContext} onClose={onCloseActiveContext} />
              ) : null}
            </Box>
          ) : null}

          {selectedScope && !activeContext ? (
            <Box
              style={{
                position: 'absolute',
                top: -14,
                left: 10,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                height: 20,
                maxWidth: 'calc(100% - 64px)',
                paddingInline: 8,
                borderRadius: 999,
                background: 'var(--mantine-color-green-6)',
                color: 'white',
              }}
            >
              <Text
                size='10px'
                fw={600}
                title={scopeTitle}
                style={{
                  minWidth: 0,
                  color: 'inherit',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {scopeTitle}
              </Text>
              <ActionIcon
                variant='transparent'
                size='xs'
                color='white'
                aria-label='Убрать контекст'
                style={{ flexShrink: 0 }}
                onClick={() => {
                  setSelectedScope(null);
                  setIsScopeBoundToDraft(false);
                  requestAnimationFrame(() => {
                    editor?.focus();
                  });
                }}
              >
                <IconX size={12} />
              </ActionIcon>
            </Box>
          ) : null}

          <Box style={{ position: 'relative' }}>
            <LexicalComposer initialConfig={initialConfig}>
              <ActivityCaptureEditorPlugin onReady={setEditor} />
              <ActivityEditablePlugin editable={!composerDisabled} />
              <ActivitySuggestionsKeyBindingsPlugin
                suggestionsOpen={suggestionsOpen}
                suggestionsCount={suggestionsCount}
                highlightedIndex={highlightedIndex}
                setHighlightedIndex={setHighlightedIndex}
                onSelectSuggestionByIndex={selectSuggestionByIndex}
                onCloseSuggestions={closeSuggestions}
                onSend={() => {
                  void send();
                }}
              />
              <OnChangePlugin
                onChange={editorState => {
                  editorState.read(() => {
                    const snapshot = readMentionSnapshot();
                    setDraft(snapshot.draft);
                    setMentionedUserIds(prev =>
                      arraysEqual(prev, snapshot.mentionedUserIds)
                        ? prev
                        : snapshot.mentionedUserIds
                    );
                    setMentionRanges(prev =>
                      mentionRangesEqual(prev, snapshot.mentionRanges)
                        ? prev
                        : snapshot.mentionRanges
                    );
                    setMentionedContactUuids(prev =>
                      stringArraysEqual(prev, snapshot.mentionedContactUuids)
                        ? prev
                        : snapshot.mentionedContactUuids
                    );
                    setContactMentionRanges(prev =>
                      contactMentionRangesEqual(prev, snapshot.contactMentionRanges)
                        ? prev
                        : snapshot.contactMentionRanges
                    );
                    setMentionContext(prev =>
                      mentionContextEqual(prev, snapshot.mentionContext)
                        ? prev
                        : snapshot.mentionContext
                    );

                    // Трекинг contact picker'а: пока он активен, весь текст
                    // в инпуте трактуется как поисковый запрос. Закрываем
                    // picker, если: (a) появился чип контакта — выбор уже
                    // случился; (b) появился перенос строки — пользователь
                    // ушёл от выбора. Иначе — обновляем query.
                    if (contactPickerContext) {
                      if (snapshot.contactMentionRanges.length > 0) {
                        setContactPickerContext(null);
                      } else if (snapshot.draft.includes('\n')) {
                        setContactPickerContext(null);
                      } else if (snapshot.draft !== contactPickerContext.query) {
                        setContactPickerContext({ query: snapshot.draft });
                      }
                    }

                    if (isScopeBoundToDraft && selectedScope) {
                      const marker = normalizeDealMentionText(selectedScope.label ?? '');
                      if (!marker || marker === '#' || !snapshot.draft.includes(marker)) {
                        setSelectedScope(null);
                        setIsScopeBoundToDraft(false);
                      }
                    }
                  });
                }}
              />
              <HistoryPlugin />
              <PlainTextPlugin
                contentEditable={
                  <ContentEditable
                    aria-label='Комментарий'
                    style={{
                      minHeight: 20,
                      maxHeight: 180,
                      overflowY: 'auto',
                      padding: '8px 68px 8px 38px',
                      borderRadius: 8,
                      outline: 'none',
                      fontSize: 14,
                      lineHeight: 1.4,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: 'var(--mantine-color-text)',
                      opacity: composerDisabled ? 0.6 : 1,
                    }}
                  />
                }
                placeholder={
                  <Text
                    size='sm'
                    c='dimmed'
                    style={{
                      pointerEvents: 'none',
                      position: 'absolute',
                      top: 8,
                      left: 38,
                      right: 72,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Написать комментарий
                  </Text>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </LexicalComposer>

            <ActionIcon
              component='button'
              type='button'
              variant={draft.trim().length === 0 ? 'light' : 'subtle'}
              color={draft.trim().length === 0 ? 'blue' : 'gray'}
              size='sm'
              radius='xl'
              aria-label='Slash-команды'
              disabled={composerDisabled || (draft.trim().length > 0 && !slashMenuOpen)}
              onClick={handleToggleSlashMenu}
              style={{
                position: 'absolute',
                left: 6,
                bottom: 6,
              }}
            >
              {slashMenuOpen ? <IconX size={16} /> : <IconSlash size={16} />}
            </ActionIcon>

            <ActionIcon
              component='button'
              type='button'
              variant='subtle'
              color='gray'
              size='sm'
              radius='xl'
              aria-label='Прикрепить файл'
              disabled={composerDisabled}
              onClick={handleOpenFilePicker}
              style={{
                position: 'absolute',
                right: 36,
                bottom: 6,
              }}
            >
              <IconPaperclip size={16} />
            </ActionIcon>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              hidden
              onChange={event => {
                const files = event.target.files;
                if (files && files.length > 0) {
                  void handleAttachFiles(files);
                }
                event.target.value = '';
              }}
            />

            <ActionIcon
              component='button'
              type='button'
              variant='filled'
              color='blue'
              size='sm'
              aria-label='Отправить'
              disabled={!draft.trim() || composerDisabled || hasUploadingFiles}
              onClick={() => void send()}
              style={{
                position: 'absolute',
                right: 6,
                bottom: 6,
              }}
            >
              <IconSend size={14} />
            </ActionIcon>
          </Box>
        </Box>

        {attachedFiles.length > 0 ? (
          <Group gap={6} wrap='wrap' mt={6}>
            {attachedFiles.map(file => (
              <Paper
                key={file.clientId}
                withBorder
                radius='sm'
                px={8}
                py={4}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: file.error
                    ? 'var(--mantine-color-red-0)'
                    : 'var(--mantine-color-gray-0)',
                  maxWidth: 240,
                }}
              >
                {file.uploading ? (
                  <Loader size={12} />
                ) : (
                  <IconPaperclip size={12} color='var(--mantine-color-gray-6)' />
                )}
                <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                  <Text
                    size='xs'
                    fw={500}
                    title={file.name}
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {file.name}
                  </Text>
                  <Text size='10px' c={file.error ? 'red' : 'dimmed'}>
                    {file.error
                      ? file.error
                      : file.uploading
                        ? 'Загрузка...'
                        : formatFileSize(file.size)}
                  </Text>
                </Stack>
                <ActionIcon
                  variant='subtle'
                  color='gray'
                  size='xs'
                  aria-label='Удалить файл'
                  onClick={() => handleRemoveAttachedFile(file.clientId)}
                >
                  <IconX size={12} />
                </ActionIcon>
              </Paper>
            ))}
          </Group>
        ) : null}
      </Box>
    </Stack>
  );
}
